import axios from 'axios';
import axiosInstance, { handleUnauthorized, AUTH_EXPIRED_EVENT } from './axiosInstance';

const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.castar.co.kr';

// Admin 전용 axios — withCredentials: true (httpOnly 쿠키 자동 첨부)
const adminHttp = axios.create({
  baseURL: BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

/**
 * 401 → 토큰 갱신 후 재시도. 갱신은 일반 API와 **같은 한 번**을 공유한다 —
 * 따로 갱신하면 먼저 끝난 쪽이 리프레시 토큰을 폐기해 다른 쪽이 로그아웃된다.
 *
 * 403은 로그인 화면으로 보내지 않는다. 로그인은 돼 있고 그 기능만 권한이 없는 것이라
 * 다시 로그인해도 풀리지 않는다. (예전에는 500이던 권한 부족이 이제 403으로 온다)
 */
adminHttp.interceptors.response.use(
  (res) => res,
  (error) => handleUnauthorized(error, (config) => adminHttp(config)),
);

// 갱신까지 실패해 세션이 끝나면 어드민 로그인으로 보낸다
if (typeof window !== 'undefined') {
  window.addEventListener(AUTH_EXPIRED_EVENT, () => {
    if (!sessionStorage.getItem('adminLoggedIn')) return;
    sessionStorage.removeItem('adminLoggedIn');
    if (window.location.pathname.startsWith('/admin')) window.location.href = '/admin';
  });
}

// ── 인증 ──────────────────────────────────────────────────────────────────────
// POST /api/login { email, password }
// 백엔드가 httpOnly 쿠키로 인증 처리 — 응답 헤더 토큰 불필요
// Response body: { userId, isTalent, isProducer, isAdmin, isQnaAdmin, profiles }
export const apiAdminLogin = async (email, password) => {
  const res = await axiosInstance.post('/api/login', { email, password });
  return res.data;
};

// POST /api/auth/logout — httpOnly 쿠키 만료
export const apiAdminLogout = async () => {
  try {
    await axiosInstance.post('/api/auth/logout');
  } catch {}
};

// ── 대시보드 통계 ─────────────────────────────────────────────────────────────
export const getStatsSummary = async () => {
  const res = await adminHttp.get('/api/admin/stats/summary');
  return res.data;
};

export const getStatsDaily = async (days = 14) => {
  const res = await adminHttp.get('/api/admin/stats/daily', { params: { days } });
  return res.data;
};

export const getStatsTopViews = async (limit = 3) => {
  const res = await adminHttp.get('/api/admin/stats/top-views', { params: { limit } });
  return res.data;
};

export const getStatsDemographics = async () => {
  const res = await adminHttp.get('/api/admin/stats/demographics');
  return res.data;
};

export const getStatsHourly = async () => {
  const res = await adminHttp.get('/api/admin/stats/hourly');
  return res.data;
};

// ── 배우 관리 ─────────────────────────────────────────────────────────────────
/** 응답 본문에서 배우 배열을 찾아낸다. 키 이름이 달라도 견디도록 후보를 순회. */
const extractActorArray = (body) => {
  if (Array.isArray(body)) return body;
  if (!body || typeof body !== 'object') return null;
  for (const key of ['actors', 'content', 'list', 'items', 'results']) {
    if (Array.isArray(body[key])) return body[key];
  }
  if (body.data) return extractActorArray(body.data);
  return null;
};

/**
 * 배우 검색
 * 응답을 { actors, total }로 정규화한다. 예전에는 res.data를 그대로 넘겨
 * `data.actors`가 없으면 화면이 조용히 빈 목록을 보여줬다.
 */
export const searchAdminActors = async ({ q = '', type = 'name', filter = 'all', page = 1, limit = 10 } = {}) => {
  const res = await adminHttp.get('/api/admin/actors', {
    params: { q: String(q ?? '').trim(), type, filter, page, limit },
  });
  const actors = extractActorArray(res.data);
  if (!actors) {
    throw new Error('배우 목록 응답 형식을 해석할 수 없습니다. 백엔드 응답 스키마를 확인해주세요.');
  }
  const body = res.data ?? {};
  const total = [body.total, body.totalElements, body.totalCount, body.count]
    .find(v => Number.isFinite(v));
  return { actors, total: total ?? actors.length };
};

/**
 * 성별·출생연도 조건에 맞는 배우 전체 조회 (이메일 추출용)
 *
 * 전용 엔드포인트(/api/admin/actors/export)를 먼저 시도하고,
 * 없거나(404/405/501) 응답이 배열이 아니면 기존 목록 API를 순회하는 폴백으로 넘어간다.
 * 응답 형태가 예상과 다를 때 조용히 0건을 반환하면 원인 파악이 불가능하므로,
 * 어느 경로로 몇 건을 받았는지를 함께 돌려준다.
 *
 * @param {{ gender?: 'ALL'|'MALE'|'FEMALE', birthYearFrom?: number|'', birthYearTo?: number|'',
 *           onProgress?: (loaded: number, total: number) => void }} opts
 * @returns {Promise<{ actors: Array, source: 'export'|'fallback', rawCount: number }>}
 */
export const fetchAdminActorsForExport = async ({
  gender = 'ALL', birthYearFrom = '', birthYearTo = '', onProgress,
} = {}) => {
  const params = {};
  if (gender && gender !== 'ALL') params.gender = gender;
  if (birthYearFrom !== '') params.birthYearFrom = birthYearFrom;
  if (birthYearTo !== '') params.birthYearTo = birthYearTo;

  try {
    const res = await adminHttp.get('/api/admin/actors/export', { params });
    const actors = extractActorArray(res.data);
    // 배열을 못 찾으면 엔드포인트가 아직 없는 것으로 보고 폴백으로 넘어간다
    // (SPA가 index.html을 200으로 돌려주는 경우 등)
    if (actors) return { actors, source: 'export', rawCount: actors.length };
  } catch (err) {
    const status = err.response?.status;
    // 권한 오류·서버 오류는 그대로 던져 화면에 노출한다
    if (status !== 404 && status !== 405 && status !== 501) throw err;
  }

  // ── 폴백: 기존 목록 API 페이지 순회 후 클라이언트에서 필터 ──
  const PAGE_SIZE = 100;
  const collected = [];
  let page = 1;
  let total = Infinity;

  while (collected.length < total) {
    const body = await adminHttp
      .get('/api/admin/actors', { params: { q: '', type: 'name', filter: 'all', page, limit: PAGE_SIZE } })
      .then(r => r.data);

    const batch = extractActorArray(body);
    if (!batch) {
      throw new Error('배우 목록 응답 형식을 해석할 수 없습니다. 백엔드 응답 스키마를 확인해주세요.');
    }
    total = Number.isFinite(body?.total) ? body.total : collected.length + batch.length;
    collected.push(...batch);
    onProgress?.(collected.length, total);

    if (batch.length === 0 || batch.length < PAGE_SIZE) break;
    page += 1;
    if (page > 200) break; // 안전장치 — 무한 루프 방지
  }

  const from = birthYearFrom === '' ? -Infinity : Number(birthYearFrom);
  const to = birthYearTo === '' ? Infinity : Number(birthYearTo);
  const filtered = collected.filter(a => {
    if (a.isTest) return false; // export 엔드포인트와 동일하게 테스트 계정 제외
    if (gender !== 'ALL' && String(a.gender ?? '').toUpperCase() !== gender) return false;
    const y = Number(a.birthYear);
    if (!Number.isFinite(y)) return birthYearFrom === '' && birthYearTo === '';
    return y >= from && y <= to;
  });

  return { actors: filtered, source: 'fallback', rawCount: collected.length };
};

export const getAdminActor = async (id) => {
  const res = await adminHttp.get(`/api/admin/actors/${id}`);
  return res.data;
};

export const updateAdminActor = async (id, data) => {
  const res = await adminHttp.put(`/api/admin/actors/${id}`, data);
  return res.data;
};

export const updateActorVisibility = async (id, value) => {
  const res = await adminHttp.patch(`/api/admin/actors/${id}/visibility`, { value });
  return res.data;
};

export const updateActorStatus = async (id, value) => {
  const res = await adminHttp.patch(`/api/admin/actors/${id}/status`, { value });
  return res.data;
};

export const getActorHistory = async (id) => {
  const res = await adminHttp.get(`/api/admin/actors/${id}/history`);
  return res.data;
};

// ── 제작사 관리 ───────────────────────────────────────────────────────────────
/**
 * 제작사 검색.
 * 응답을 { companies, total }로 정규화한다 — 키가 다르면 화면이 조용히 0건이 되던 문제 방지.
 */
export const searchAdminCompanies = async ({ q = '', page = 1, limit = 10 } = {}) => {
  const res = await adminHttp.get('/api/admin/companies', {
    // 빈 검색어는 키째 뺀다 (공고 목록과 동일)
    params: { q: String(q ?? '').trim() || undefined, page, limit },
  });
  const body = res.data ?? {};
  const list = Array.isArray(body) ? body
    : (body.companies ?? body.content ?? body.list ?? body.items ?? null);
  if (!Array.isArray(list)) {
    throw new Error('제작사 목록 응답 형식을 해석할 수 없습니다. 백엔드 응답 스키마를 확인해주세요.');
  }
  const total = [body.total, body.totalElements, body.totalCount, body.count]
    .find(v => Number.isFinite(v));
  return { companies: list, total: total ?? list.length };
};

export const getAdminCompany = async (id) => {
  const res = await adminHttp.get(`/api/admin/companies/${id}`);
  return res.data;
};

export const updateAdminCompany = async (id, data) => {
  const res = await adminHttp.put(`/api/admin/companies/${id}`, data);
  return res.data;
};

export const getCompanyPostings = async (id) => {
  const res = await adminHttp.get(`/api/admin/companies/${id}/postings`);
  return res.data;
};

// ── 공고 관리 ─────────────────────────────────────────────────────────────────
/**
 * 공고 검색
 * 응답을 { postings, total }로 정규화한다 — 키가 다르면 화면이 조용히 0건이 되던 문제 방지.
 */
export const searchAdminPostings = async ({ q = '', status = '', page = 1, limit = 20 } = {}) => {
  const res = await adminHttp.get('/api/admin/postings', {
    // 빈 값은 키째 뺀다. status=''를 그대로 보내면 서버가 enum으로 파싱하다 터진다.
    params: {
      q: String(q ?? '').trim() || undefined,
      status: status || undefined,
      page,
      limit,
    },
  });
  const body = res.data ?? {};
  const list = Array.isArray(body) ? body
    : (body.postings ?? body.content ?? body.list ?? body.items ?? null);
  if (!Array.isArray(list)) {
    throw new Error('공고 목록 응답 형식을 해석할 수 없습니다. 백엔드 응답 스키마를 확인해주세요.');
  }
  const total = [body.total, body.totalElements, body.totalCount, body.count]
    .find(v => Number.isFinite(v));
  return { postings: list, total: total ?? list.length };
};

export const getAdminPosting = async (id) => {
  const res = await adminHttp.get(`/api/admin/postings/${id}`);
  return res.data;
};

/**
 * 공고 지원자 목록.
 * 배열이 아닌 형태로 와도 화면이 통째로 실패하지 않도록 공고 목록과 같은 방식으로 정규화한다.
 */
export const getPostingApplicants = async (id) => {
  const res = await adminHttp.get(`/api/admin/postings/${id}/applicants`);
  const body = res.data ?? {};
  const list = Array.isArray(body) ? body
    : (body.applicants ?? body.applications ?? body.content ?? body.list ?? body.items ?? null);
  if (!Array.isArray(list)) {
    throw new Error('지원자 목록 응답 형식을 해석할 수 없습니다. 백엔드 응답 스키마를 확인해주세요.');
  }
  return list;
};

export const updateApplicant = async (postingId, applicantId, data) => {
  const res = await adminHttp.patch(`/api/admin/postings/${postingId}/applicants/${applicantId}`, data);
  return res.data;
};

// ── 대행 제작사 계정 ──────────────────────────────────────────────────────────
/**
 * 서버가 보낸 실패 사유를 뽑아낸다.
 *
 * 400은 원인이 제각각(중복 이메일, 형식 오류, 필수값 누락)이라
 * 상태 코드만 보여주면 어디를 고쳐야 할지 알 수 없다.
 * Spring의 기본 오류 본문과 검증 오류 배열을 모두 받아준다.
 */
export const extractApiErrorMessage = (err, fallback = '') => {
  const d = err?.response?.data;
  if (!d) return fallback;
  if (typeof d === 'string') return d.trim().slice(0, 300) || fallback;

  const fieldErrors = [d.errors, d.fieldErrors, d.violations].find(Array.isArray);
  if (fieldErrors?.length) {
    const lines = fieldErrors
      .map(e => [e.field, e.defaultMessage ?? e.message ?? e.reason].filter(Boolean).join(' '))
      .filter(Boolean);
    if (lines.length) return lines.join(', ');
  }

  return d.message ?? d.error ?? d.detail ?? fallback;
};

/** 백엔드 미배포 구간을 화면에서 구분하기 위한 헬퍼 */
export const isAdminApiUnavailable = (err) => {
  const s = err?.response?.status;
  return s === 404 || s === 405 || s === 501;
};

/**
 * 대행 계정 생성 — 제작사 계정 + ProducerProfile을 한 번에 만든다.
 * tempPassword는 이 응답에서만 평문으로 돌아오고 이후 조회할 수 없다.
 * @returns {{ userId, producerProfileId, email, tempPassword }}
 */
export const createManagedProducer = async (data) => {
  const res = await adminHttp.post('/api/admin/managed-producers', data);
  return res.data;
};

export const getManagedProducers = async ({ page = 1, limit = 20 } = {}) => {
  const res = await adminHttp.get('/api/admin/managed-producers', { params: { page, limit } });
  const body = res.data ?? {};
  const list = Array.isArray(body) ? body
    : (body.accounts ?? body.content ?? body.list ?? body.items ?? null);
  if (!Array.isArray(list)) {
    throw new Error('대행 계정 목록 응답 형식을 해석할 수 없습니다.');
  }
  const total = [body.total, body.totalElements, body.totalCount, body.count]
    .find(v => Number.isFinite(v));
  return { accounts: list, total: total ?? list.length };
};

/** 임시 비밀번호 재발급 — mustChangePassword가 다시 true가 된다 */
export const resetManagedProducerPassword = async (userId) => {
  const res = await adminHttp.post(`/api/admin/managed-producers/${userId}/reset-password`);
  return res.data; // { tempPassword }
};

export const updateManagedProducerStatus = async (userId, status) => {
  const res = await adminHttp.patch(`/api/admin/managed-producers/${userId}/status`, { status });
  return res.data;
};

/**
 * 대행 계정 삭제 — 되돌릴 수 없다.
 *
 * 잘못 만든 계정을 지우기 위한 것이다. 쓰던 계정을 잠시 막는 것이라면 '정지'를 쓴다.
 * 공고가 남아 있으면 서버가 409로 막는다 — 공고를 먼저 다른 제작사로 이관해야 한다.
 */
export const deleteManagedProducer = async (userId) => {
  await adminHttp.delete(`/api/admin/managed-producers/${userId}`);
};

/**
 * 어드민이 특정 제작사 소유로 공고를 생성한다 (대행 공고).
 * 일반 생성과 페이로드가 같고 producerProfileId만 추가된다.
 */
/**
 * 이미 올라간 공고의 소유 제작사를 교체한다 (대행 이관).
 *
 * 공고 ID·URL·지원자·조회수는 그대로 유지되고 소유자만 바뀐다.
 * 지원자 정보는 새 소유자에게 전부 넘어가지만, 메시지 스레드는
 * 원래 userId에 묶여 있어 따라가지 않는다.
 * 같은 API로 원래 producerProfileId를 다시 보내면 되돌릴 수 있다.
 */
export const transferPostingOwner = async (postingId, producerProfileId) => {
  await adminHttp.patch(`/api/admin/postings/${postingId}/owner`, { producerProfileId });
};

export const createAdminCasting = async (producerProfileId, data) => {
  const res = await adminHttp.post('/api/admin/castings', { ...data, producerProfileId });
  return res.data;
};

// ── 테스트 계정 ───────────────────────────────────────────────────────────────
// PATCH /api/admin/users/{userId}/test-mode  body: { isTest: boolean }
/**
 * 어드민 권한 부여/해제
 * PATCH /api/admin/users/{userId}/role  body: { isAdmin: boolean }
 * (백엔드 미구현 상태일 수 있어 404/405를 호출부에서 구분해 안내한다)
 */
export const updateAdminRole = async (userId, isAdmin) => {
  const res = await adminHttp.patch(`/api/admin/users/${userId}/role`, { isAdmin });
  return res.data;
};

export const updateTestMode = async (userId, isTest) => {
  const res = await adminHttp.patch(`/api/admin/users/${userId}/test-mode`, { isTest });
  return res.data;
};
